
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { 
  Shield, 
  RefreshCw, 
  Search, 
  Calendar,
  User,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Activity
} from 'lucide-react';

interface AuditLog {
  id: string;
  actor_id: string;
  action: string;
  entity: string;
  entity_id: string;
  meta: any;
  created_at: string;
  timestamp: string;
}

const SecurityAuditLog = () => {
  const { profile } = useAuth();
  const { t } = useLanguage();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [timeRange, setTimeRange] = useState<string>('7d');

  const fetchAuditLogs = async () => {
    try {
      console.log('Fetching audit logs...');
      setLoading(true);

      let query = supabase
        .from('audit_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      // Apply time filter
      if (timeRange !== 'all') {
        const now = new Date();
        let startDate: Date;
        
        switch (timeRange) {
          case '1d':
            startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
            break;
          case '7d':
            startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            break;
          case '30d':
            startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            break;
          default:
            startDate = new Date(0);
        }
        
        query = query.gte('created_at', startDate.toISOString());
      }

      // Apply action filter
      if (filter !== 'all') {
        query = query.eq('action', filter);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching audit logs:', error);
        toast.error('Failed to fetch audit logs');
        return;
      }

      console.log('Fetched audit logs:', data?.length);
      setLogs(data || []);
    } catch (error) {
      console.error('Exception fetching audit logs:', error);
      toast.error('Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (profile && ['admin', 'superadmin'].includes(profile.role)) {
      fetchAuditLogs();
    }
  }, [profile, filter, timeRange]);

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'user_created': return <User className="h-4 w-4 text-green-500" />;
      case 'role_update': return <Shield className="h-4 w-4 text-blue-500" />;
      case 'login_attempt': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed_login': return <XCircle className="h-4 w-4 text-red-500" />;
      case 'security_violation': return <AlertTriangle className="h-4 w-4 text-red-500" />;
      default: return <Activity className="h-4 w-4 text-gray-500" />;
    }
  };

  const getActionBadgeVariant = (action: string) => {
    switch (action) {
      case 'user_created': return 'default';
      case 'role_update': return 'secondary';
      case 'login_attempt': return 'outline';
      case 'failed_login': return 'destructive';
      case 'security_violation': return 'destructive';
      default: return 'outline';
    }
  };

  const formatAction = (action: string) => {
    const actionMap: { [key: string]: string } = {
      'user_created': t('user_created', 'إنشاء مستخدم', 'User Created'),
      'role_update': t('role_updated', 'تحديث الدور', 'Role Updated'),
      'login_attempt': t('login_attempt', 'محاولة دخول', 'Login Attempt'),
      'failed_login': t('failed_login', 'فشل الدخول', 'Failed Login'),
      'security_violation': t('security_violation', 'انتهاك أمني', 'Security Violation'),
      'kyc_update': t('kyc_updated', 'تحديث التحقق', 'KYC Updated'),
      'password_change': t('password_changed', 'تغيير كلمة المرور', 'Password Changed')
    };
    
    return actionMap[action] || action;
  };

  const formatMeta = (meta: any) => {
    if (!meta || typeof meta !== 'object') return null;
    
    return Object.entries(meta).map(([key, value]) => (
      <span key={key} className="text-xs text-muted-foreground">
        {key}: {String(value)}
      </span>
    ));
  };

  if (!profile || !['admin', 'superadmin'].includes(profile.role)) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-muted-foreground">
            {t('access_denied', 'غير مسموح بالوصول', 'Access denied')}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            <CardTitle>{t('security_audit_log', 'سجل المراجعة الأمنية', 'Security Audit Log')}</CardTitle>
          </div>
          <Button onClick={fetchAuditLogs} size="sm" disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {t('refresh', 'تحديث', 'Refresh')}
          </Button>
        </div>
        <CardDescription>
          {t('audit_log_desc', 'تتبع جميع الأنشطة الأمنية والتغييرات الحساسة', 'Track all security activities and sensitive changes')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue placeholder={t('filter_by_action', 'تصفية حسب الإجراء', 'Filter by action')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('all_actions', 'جميع الإجراءات', 'All Actions')}</SelectItem>
              <SelectItem value="user_created">{t('user_created', 'إنشاء مستخدم', 'User Created')}</SelectItem>
              <SelectItem value="role_update">{t('role_updated', 'تحديث الدور', 'Role Updated')}</SelectItem>
              <SelectItem value="login_attempt">{t('login_attempt', 'محاولة دخول', 'Login Attempt')}</SelectItem>
              <SelectItem value="failed_login">{t('failed_login', 'فشل الدخول', 'Failed Login')}</SelectItem>
              <SelectItem value="security_violation">{t('security_violation', 'انتهاك أمني', 'Security Violation')}</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue placeholder={t('time_range', 'النطاق الزمني', 'Time range')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1d">{t('last_24_hours', 'آخر 24 ساعة', 'Last 24 hours')}</SelectItem>
              <SelectItem value="7d">{t('last_7_days', 'آخر 7 أيام', 'Last 7 days')}</SelectItem>
              <SelectItem value="30d">{t('last_30_days', 'آخر 30 يوم', 'Last 30 days')}</SelectItem>
              <SelectItem value="all">{t('all_time', 'كل الأوقات', 'All time')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Audit Log Entries */}
        <div className="space-y-3">
          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
              <p className="text-muted-foreground mt-2">
                {t('loading_logs', 'تحميل السجلات...', 'Loading logs...')}
              </p>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-8">
              <Search className="h-8 w-8 mx-auto text-muted-foreground" />
              <p className="text-muted-foreground mt-2">
                {t('no_logs_found', 'لم يتم العثور على سجلات', 'No logs found')}
              </p>
            </div>
          ) : (
            logs.map((log) => (
              <div key={log.id} className="flex items-start gap-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                <div className="flex-shrink-0 mt-1">
                  {getActionIcon(log.action)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant={getActionBadgeVariant(log.action)}>
                      {formatAction(log.action)}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      {log.entity}: {log.entity_id}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    <span>{new Date(log.created_at).toLocaleString()}</span>
                    <User className="h-3 w-3 ml-2" />
                    <span>Actor: {log.actor_id.slice(0, 8)}...</span>
                  </div>
                  {log.meta && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {formatMeta(log.meta)}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Summary Stats */}
        {logs.length > 0 && (
          <div className="pt-4 border-t">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-green-600">
                  {logs.filter(l => ['user_created', 'login_attempt'].includes(l.action)).length}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t('successful_actions', 'إجراءات ناجحة', 'Successful Actions')}
                </div>
              </div>
              <div>
                <div className="text-2xl font-bold text-red-600">
                  {logs.filter(l => ['failed_login', 'security_violation'].includes(l.action)).length}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t('security_events', 'أحداث أمنية', 'Security Events')}
                </div>
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-600">
                  {logs.filter(l => l.action === 'role_update').length}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t('role_changes', 'تغييرات الأدوار', 'Role Changes')}
                </div>
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-600">
                  {new Set(logs.map(l => l.actor_id)).size}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t('unique_actors', 'فاعلين فريدين', 'Unique Actors')}
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SecurityAuditLog;
